import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clearPendingPayment,
  clearPaymentResult,
  generateBookingId,
  loadBookings,
  loadPendingPayment,
  loadPaymentResult,
  parseStorageValue,
  saveBookings,
  savePendingPayment,
  savePaymentResult,
  storageKeys,
} from "../utils/bookingStorage";

const BookingContext = createContext({
  bookings: [],
  pendingPayment: null,
  latestResult: null,
  awaitingPayment: false,
  activeReference: null,
  registerPendingPayment: () => null,
  completePendingPayment: () => null,
  acknowledgeResult: () => {},
  resetPendingPayment: () => {},
  recordWebCheckIn: () => {},
  recordWebCheckOut: () => {},
});

const safeLoadBookings = () => {
  try {
    return loadBookings();
  } catch (error) {
    console.warn("Failed to derive initial booking state", error);
    return [];
  }
};

const safeLoadPendingPayment = () => {
  try {
    return loadPendingPayment();
  } catch (error) {
    console.warn("Failed to read pending payment from storage", error);
    return null;
  }
};

export const BookingProvider = ({ children }) => {
  const [bookings, setBookings] = useState(safeLoadBookings);
  const [pendingPayment, setPendingPaymentState] = useState(safeLoadPendingPayment);
  const [awaitingPayment, setAwaitingPayment] = useState(() =>
    Boolean(safeLoadPendingPayment())
  );
  const [latestResult, setLatestResult] = useState(null);
  const [activeReference, setActiveReference] = useState(
    () => pendingPayment?.reference || null
  );

  useEffect(() => {
    saveBookings(bookings);
  }, [bookings]);

  useEffect(() => {
    if (pendingPayment) {
      savePendingPayment(pendingPayment);
    } else {
      clearPendingPayment();
    }
  }, [pendingPayment]);

  const ingestResult = useCallback((result) => {
    if (!result) return;
    setLatestResult(result);
    setAwaitingPayment(false);
    setPendingPaymentState(null);
    setActiveReference(result.reference || null);
    clearPendingPayment();
    if (result.booking) {
      setBookings((prev) => {
        const filtered = prev.filter((item) => item.id !== result.booking.id);
        return [result.booking, ...filtered];
      });
    }
  }, []);

  useEffect(() => {
    const storedResult = loadPaymentResult();
    if (storedResult) {
      clearPaymentResult();
      ingestResult(storedResult);
    }
  }, [ingestResult]);

  useEffect(() => {
    if (typeof window === "undefined") return () => {};
    const handleStorage = (event) => {
      if (!event.key) return;
      if (event.key === storageKeys.paymentResult && event.newValue) {
        const parsed = parseStorageValue(event.newValue);
        if (parsed) {
          ingestResult(parsed);
          clearPaymentResult();
        }
      }
      if (event.key === storageKeys.pendingPayment) {
        const parsed = parseStorageValue(event.newValue);
        setPendingPaymentState(parsed);
        setAwaitingPayment(Boolean(parsed));
        setActiveReference(parsed?.reference || null);
      }
      if (event.key === storageKeys.bookings && event.newValue) {
        const parsed = parseStorageValue(event.newValue);
        if (Array.isArray(parsed)) {
          setBookings(parsed);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [ingestResult]);

  const registerPendingPayment = useCallback((payload) => {
    const reference = payload?.reference || `PAY-${Date.now()}`;
    const enriched = { ...payload, reference, createdAt: payload?.createdAt || new Date().toISOString() };
    setPendingPaymentState(enriched);
    setAwaitingPayment(true);
    setActiveReference(reference);
    setLatestResult(null);
    savePendingPayment(enriched);
    clearPaymentResult();
    return enriched;
  }, []);

  const resetPendingPayment = useCallback(() => {
    setPendingPaymentState(null);
    setAwaitingPayment(false);
    setActiveReference(null);
    clearPendingPayment();
  }, []);

  const completePendingPayment = useCallback(
    ({ status, paymentMode, notes }) => {
      if (!pendingPayment) return null;
      const timestamp = new Date().toISOString();
      const bookingStatus = status === "success" ? "confirmed" : "failed";
      const bookingId = generateBookingId();
      const productType =
        pendingPayment.productType || (pendingPayment.stays ? "nightStay" : "experience");
      const items = pendingPayment.items || pendingPayment.stays || null;
      const bookingRecord = {
        id: bookingId,
        status: bookingStatus,
        createdAt: timestamp,
        contact: pendingPayment.contact,
        guests: pendingPayment.guests,
        journey: pendingPayment.journey,
        primaryGuest: pendingPayment.primaryGuest,
        summary: pendingPayment.summary,
        stays: pendingPayment.stays,
        destination: pendingPayment.destination,
        productType,
        payment: {
          gateway: pendingPayment.gateway,
          mode: paymentMode || pendingPayment.gateway?.name || "online",
          baseAmount: pendingPayment.baseAmount,
          surcharge: pendingPayment.surcharge,
          totalAmount: pendingPayment.totalAmount,
          reference: pendingPayment.reference,
        },
      };
      if (items) {
        bookingRecord.items = items;
      }
      if (notes) {
        bookingRecord.notes = notes;
      }
      if (bookingStatus === "failed" && !notes) {
        bookingRecord.notes = "Payment marked as failed.";
      }
      const resultPayload = {
        status,
        reference: pendingPayment.reference,
        timestamp,
        bookingId,
        booking: bookingRecord,
        notes: bookingRecord.notes || null,
        productType,
      };
      ingestResult(resultPayload);
      savePaymentResult(resultPayload);
      return resultPayload;
    },
    [pendingPayment, ingestResult]
  );

  const updateBookingRecord = useCallback((bookingId, updater) => {
    if (!bookingId || typeof updater !== "function") return;
    setBookings((prev) => {
      let mutated = false;
      const next = prev.map((booking) => {
        if (booking.id !== bookingId) {
          return booking;
        }
        const updated = updater(booking);
        if (updated && updated !== booking) {
          mutated = true;
          return updated;
        }
        return booking;
      });
      return mutated ? next : prev;
    });
  }, []);

  const recordWebCheckIn = useCallback(
    (bookingId, payload) => {
      if (!bookingId || !payload) return;
      const submittedAt = payload.submittedAt || new Date().toISOString();
      updateBookingRecord(bookingId, (booking) => ({
        ...booking,
        webCheckIn: {
          ...payload,
          submittedAt,
        },
      }));
    },
    [updateBookingRecord]
  );

  const recordWebCheckOut = useCallback(
    (bookingId, payload) => {
      if (!bookingId || !payload) return;
      const submittedAt = payload.submittedAt || new Date().toISOString();
      updateBookingRecord(bookingId, (booking) => ({
        ...booking,
        webCheckOut: {
          ...payload,
          submittedAt,
        },
      }));
    },
    [updateBookingRecord]
  );

  const acknowledgeResult = useCallback(() => {
    setLatestResult(null);
  }, []);

  const value = useMemo(
    () => ({
      bookings,
      pendingPayment,
      latestResult,
      awaitingPayment,
      activeReference,
      registerPendingPayment,
      completePendingPayment,
      acknowledgeResult,
      resetPendingPayment,
      recordWebCheckIn,
      recordWebCheckOut,
      updateBookingRecord,
    }),
    [
      bookings,
      pendingPayment,
      latestResult,
      awaitingPayment,
      activeReference,
      registerPendingPayment,
      completePendingPayment,
      acknowledgeResult,
      resetPendingPayment,
      recordWebCheckIn,
      recordWebCheckOut,
      updateBookingRecord,
    ]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBooking = () => useContext(BookingContext);

export default BookingContext;
